import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Row, Col, Card, Badge, Table, Spinner, Alert, ProgressBar, Form, Button, Modal, Tabs, Tab } from 'react-bootstrap';
import Select from '../components/common/Select';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import DatePeriodNavigation, { PeriodChangeEvent } from '../components/common/DatePeriodNavigation';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import CommissionPaymentsTable from '../components/CommissionPaymentsTable';
import CommissionableInvoicesTable from '../components/CommissionableInvoicesTable';
import { ProjectionPicker, ProjectionFormPanel } from '../components/ProjectionsPanel';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import type { CommissionableInvoiceRow, CommissionPeriodSummary, Invoice, CommissionableInvoicesSummary } from '../api/generated/model';
import { RootState } from '../store';
import {
  useGetCommissionsUserId,
  getCommissionsUserIdInvoices,
  useGetCommissionPayments,
  useGetCommissionsUserIdPayments,
  useGetUsersId,
  useGetUsersUserIdSalesQuota,
  usePutUsersUserIdSalesQuota,
  deleteCommissionsQuotasId,
  useGetCommissionsUploads,
  getGetCommissionsUploadsQueryKey,
  postCommissionsUploadsMapBatch,
  deleteCommissionsUploadsId,
  deleteCommissionsUploads,
  postCommissionsImportConfig,
  getGetUsersUserIdSalesQuotaQueryKey,
  getGetCommissionPaymentsQueryKey,
  getBankTransactionsTransactionId,
  useGetCommissionsStrandedBankTransactions,
  getGetCommissionsStrandedBankTransactionsQueryKey,
  putPayeesIdUser,
  useGetCommissionsDiff,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  SalesQuota,
  SalesQuotaLinkConflict,
  QuotaConfigUpload,
  QuotaConfigUploadView,
  User,
  BankTransaction,
  StrandedCounterparty,
} from '../api/generated/model';
import { useAllUsers } from '../hooks/useAllUsers';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useNotifications } from '../contexts/NotificationContext';

const validPeriods = ['month', 'quarter', 'year', 'custom', 'all'] as const;
type Period = typeof validPeriods[number];

function formatLocalDate(s?: string): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}

function parseDate(s: string | null): string | null {
  if (!s) return null;
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]), m = Number(parts[1]), day = Number(parts[2]);
  if (isNaN(y) || isNaN(m) || isNaN(day)) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear(), m = d.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  return { start_date: start.toLocaleDateString('en-CA'), end_date: end.toLocaleDateString('en-CA') };
}

function parseInitialDateParams(params: URLSearchParams): { period: Period; start_date: string; end_date: string } {
  const rawPeriod = params.get('period');
  const period: Period = rawPeriod && (validPeriods as readonly string[]).includes(rawPeriod)
    ? rawPeriod as Period : 'month';
  // 'all' has no date bounds — round-trip as empty strings. Without this
  // branch, parseInitialDateParams would fall through to monthRange()
  // and silently reset the page to the current month on reload of a
  // `?period=all` URL.
  if (period === 'all') {
    return { period, start_date: '', end_date: '' };
  }
  let startDate = parseDate(params.get('start_date'));
  let endDate = parseDate(params.get('end_date'));
  const defaults = monthRange(new Date());
  if (!startDate) startDate = defaults.start_date;
  if (!endDate) endDate = defaults.end_date;
  if (startDate > endDate) [startDate, endDate] = [endDate, startDate];
  return { period, start_date: startDate, end_date: endDate };
}

function formatCurrency(value: string | undefined): string {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function formatPercent(value: string | undefined): string {
  if (!value) return '0%';
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  return (num * 100).toFixed(1) + '%';
}

// ============================================================================
// Main page — top-level tabs: me / team / config
// ============================================================================

const CommissionsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'finance'
    || user?.role === 'vp_sales' || user?.role === 'sales_manager';

  // Issue count for the tab badge. Two flavors so the right query runs based
  // on the viewer's role; the disabled flag stops the unused one from firing.
  // Single-page fetch (limit=1) — we only need pagination.total. The cursor
  // library requires sort_field when no cursor is supplied.
  const issuesAdminQuery = useGetCommissionPayments(
    { has_validation_errors: true, limit: 1, sort_field: 'paid_at', sort_direction: 'desc' } as any,
    { query: { enabled: !!user && isManagerOrAdmin } }
  );
  const issuesUserQuery = useGetCommissionsUserIdPayments(
    user?.id || '',
    { has_validation_errors: true, limit: 1, sort_field: 'paid_at', sort_direction: 'desc' } as any,
    { query: { enabled: !!user?.id && !isManagerOrAdmin } }
  );
  // Stranded BTs only surface to admin/manager. Counted in the badge so
  // a single Issues number reflects everything the operator needs to act
  // on (attribution issues + unlinked-payee BTs).
  const strandedQuery = useGetCommissionsStrandedBankTransactions(
    { query: { enabled: !!user && isManagerOrAdmin } }
  );
  const strandedCount = (strandedQuery.data as any)?.data?.length || 0;
  const cpIssueCount = isManagerOrAdmin
    ? (issuesAdminQuery.data as any)?.pagination?.total
    : (issuesUserQuery.data as any)?.pagination?.total;
  const issueCount = (typeof cpIssueCount === 'number' ? cpIssueCount : 0)
    + (isManagerOrAdmin ? strandedCount : 0);

  const issuesTitle = (
    <>
      Issues
      {issueCount > 0 && (
        <Badge bg="warning" text="dark" className="ms-2">{issueCount}</Badge>
      )}
    </>
  );

  const activeTab = tab && ['me', 'issues', 'team', 'payments', 'imports', 'config'].includes(tab) ? tab : 'me';

  // Redirect invalid tabs (only after user role is loaded)
  useEffect(() => {
    if (!user) return; // Wait for auth to load
    if (!tab) {
      navigate('/commissions/me', { replace: true });
    } else if (!['me', 'issues', 'team', 'payments', 'imports', 'config'].includes(tab)) {
      navigate('/commissions/me', { replace: true });
    } else if ((tab === 'team' || tab === 'payments' || tab === 'imports' || tab === 'config') && !isManagerOrAdmin) {
      navigate('/commissions/me', { replace: true });
    }
  }, [tab, user, isManagerOrAdmin, navigate]);

  return (
    <DashboardLayout title="Commissions" scrollable>
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => k && navigate(`/commissions/${k}`)}
        mountOnEnter
        unmountOnExit
      >
        <Tab eventKey="me" title="My Commissions">
          <CommissionView userId="me" />
        </Tab>
        <Tab eventKey="issues" title={issuesTitle}>
          <IssuesView />
        </Tab>
        {isManagerOrAdmin && (
          <Tab eventKey="team" title="My Team">
            <TeamView />
          </Tab>
        )}
        {isManagerOrAdmin && (
          <Tab eventKey="payments" title="Payments">
            <PaymentsView />
          </Tab>
        )}
        {isManagerOrAdmin && (
          <Tab eventKey="config" title="Quota Configuration">
            <ConfigView />
          </Tab>
        )}
      </Tabs>
    </DashboardLayout>
  );
};

// ============================================================================
// Commission view for a single user (used by "me" tab and when viewing a team member)
// ============================================================================

// Generate list of month start dates within a range
function getMonthsInRange(startDate: string, endDate: string): string[] {
  const months: string[] = [];
  const [sy, sm] = startDate.split('-').map(Number);
  const [ey, em] = endDate.split('-').map(Number);
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    months.push(`${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-01`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

function monthEndDate(monthStart: string): string {
  const [y, m] = monthStart.split('-').map(Number);
  const end = new Date(y, m, 0);
  return end.toLocaleDateString('en-CA');
}

function monthLabel(monthStart: string): string {
  const [y, m] = monthStart.split('-').map(Number);
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[m - 1]} ${y}`;
}

// CommissionDiffBadge renders a small link-styled chip showing the
// pending-review diff count for the viewed user, deep-linking into
// /commissions/audit pre-filtered by that user. Hidden when count is 0
// or the user is unknown. The cursor's role-scoped filter (PR 1) means
// admin sees per-user counts, sales_manager sees their team's user
// counts, and salesperson sees their own — same endpoint, same filter,
// scope auto-applied server-side.
const CommissionDiffBadge: React.FC<{ userId: string }> = ({ userId }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const resolvedUserId = userId === 'me' ? user?.id : userId;
  const { data: viewedUser } = useGetUsersId(resolvedUserId || '', {
    query: { enabled: !!resolvedUserId && userId !== 'team' },
  });
  const fullName = viewedUser
    ? `${viewedUser.first_name || ''} ${viewedUser.last_name || ''}`.trim()
    : '';
  // Pending = diffs the operator hasn't yet marked Expected or
  // Investigate. Once everything is reviewed the badge disappears.
  const { data: diffResp } = useGetCommissionsDiff(
    // Only pagination.total is used, but the cursor library still requires a
    // sort on any request without a cursor. Without one this 400s, count falls
    // back to 0, and the badge silently never renders.
    {
      user_name: fullName,
      review_status: 'pending' as any,
      sort_field: 'user_name' as any,
      sort_direction: 'asc' as any,
      limit: 1,
    } as any,
    { query: { enabled: !!fullName } }
  );
  const count = (diffResp as any)?.pagination?.total ?? 0;
  if (!fullName || count === 0) return null;
  const href = `/commissions/audit?user_name=${encodeURIComponent(fullName)}&review_status=pending`;
  return (
    <Link to={href} className="text-decoration-none">
      <Badge bg="warning" text="dark" title="Open the Commissions Diff Audit page filtered to this user and pending reviews.">
        <i className="bi bi-clipboard2-data me-1" />
        {count} calculation {count === 1 ? 'difference' : 'differences'} →
      </Badge>
    </Link>
  );
};

const CommissionView: React.FC<{ userId: string }> = ({ userId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [initialDateParams] = useState(() => parseInitialDateParams(searchParams));
  const [period, setPeriod] = useState<Period>(initialDateParams.period);
  const [dateRange, setDateRange] = useState({
    start_date: initialDateParams.start_date,
    end_date: initialDateParams.end_date,
  });
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Sync dates to URL — only while this view is actually on its own tab.
  // CommissionView is rendered on /commissions/me and inside TeamView on /commissions/team.
  // The pathname guard prevents the outgoing view from re-writing URL during a tab switch.
  useEffect(() => {
    if (location.pathname !== '/commissions/me' && location.pathname !== '/commissions/team') return;
    const params = new URLSearchParams(searchParams);
    params.set('period', period);
    if (dateRange.start_date) params.set('start_date', dateRange.start_date);
    if (dateRange.end_date) params.set('end_date', dateRange.end_date);
    if (userId !== 'me') params.set('user', userId);
    else params.delete('user');
    setSearchParams(params, { replace: true });
  }, [period, dateRange, userId, location.pathname, searchParams, setSearchParams]);

  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode as Period);
    setDateRange(event.dateRange);
    setSelectedMonth(null);
  };

  // Determine if this is a multi-month view
  const months = getMonthsInRange(dateRange.start_date, dateRange.end_date);
  const isMultiMonth = months.length > 1;

  // For single month or when a month is selected from multi-month view
  const activeMonthStart = isMultiMonth
    ? (selectedMonth || null)
    : dateRange.start_date;
  const activeMonthEnd = activeMonthStart ? monthEndDate(activeMonthStart) : dateRange.end_date;

  return (
    <>
      <div className="bg-body border rounded p-2 mb-3 d-flex align-items-center gap-3 flex-wrap">
        <DatePeriodNavigation
          onPeriodChange={handlePeriodChange}
          initialDateRange={{ start_date: initialDateParams.start_date, end_date: initialDateParams.end_date }}
          initialNavigationMode={initialDateParams.period}
        />
        <CommissionDiffBadge userId={userId} />
      </div>

      {isMultiMonth ? (
        selectedMonth ? (
          <>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => setSelectedMonth(null)}>
              <i className="bi bi-arrow-left me-1"></i>Back to Overview
            </Button>
            <h5 className="mb-3">{monthLabel(selectedMonth)}</h5>
            <SingleMonthView userId={userId} monthStart={selectedMonth} monthEnd={monthEndDate(selectedMonth)} />
          </>
        ) : (
          <MultiMonthOverview userId={userId} startDate={dateRange.start_date} endDate={dateRange.end_date} onSelectMonth={setSelectedMonth} />
        )
      ) : (
        <SingleMonthView userId={userId} monthStart={dateRange.start_date} monthEnd={dateRange.end_date} />
      )}
    </>
  );
};

// Overview table showing one row per month.
//
// One API call returns the per-period summaries for the entire date range;
// each row reads from the array. No per-row loop.
const MultiMonthOverview: React.FC<{
  userId: string;
  startDate: string;
  endDate: string;
  onSelectMonth: (month: string) => void;
}> = ({ userId, startDate, endDate, onSelectMonth }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  const { data, isLoading } = useGetCommissionsUserId(
    userId,
    { start_date: startDate, end_date: endDate, sort_field: 'period_start' as any, sort_direction: 'asc' as any, limit: 60 },
    { query: { enabled: !!startDate && !!endDate } }
  );

  const periods = data?.data || [];

  const periodKey = (p: CommissionPeriodSummary): string =>
    p.period_start ? String(p.period_start) : '';

  const columns: ColumnConfig<CommissionPeriodSummary>[] = [
    {
      key: 'period_start',
      label: 'Period',
      width: 150,
      sortable: true,
      renderCell: (p) => <span className="fw-semibold">{monthLabel(periodKey(p))}</span>,
    },
    {
      key: 'quota',
      label: 'Quota',
      width: 110,
      sortable: true,
      sortValue: (p) => (p.roles || []).reduce((s, r) => s + parseFloat(r.quota_amount || '0'), 0),
      renderCell: (p) => {
        const total = (p.roles || []).reduce((s, r) => s + parseFloat(r.quota_amount || '0'), 0);
        return <span className="d-block text-end">{formatCurrency(total.toString())}</span>;
      },
    },
    {
      key: 'revenue_received',
      label: 'Revenue Received',
      width: 140,
      sortable: true,
      sortValue: (p) => (p.roles || []).reduce((s, r) => s + parseFloat(r.total_revenue_received || '0'), 0),
      renderCell: (p) => {
        const total = (p.roles || []).reduce((s, r) => s + parseFloat(r.total_revenue_received || '0'), 0);
        return <span className="d-block text-end">{formatCurrency(total.toString())}</span>;
      },
    },
    {
      key: 'total_commissions',
      label: 'Commissions',
      width: 120,
      sortable: true,
      sortValue: (p) => parseFloat(p.total_commissions || '0'),
      renderCell: (p) => <span className="d-block text-end">{formatCurrency(p.total_commissions)}</span>,
    },
    {
      key: 'commissions_paid',
      label: 'Paid',
      width: 110,
      sortable: true,
      sortValue: (p) => parseFloat(p.commissions_paid || '0'),
      renderCell: (p) => <span className="d-block text-end text-success">{formatCurrency(p.commissions_paid)}</span>,
    },
    {
      key: 'commissions_due',
      label: 'Due',
      width: 110,
      sortable: true,
      sortValue: (p) => parseFloat(p.commissions_due || '0'),
      renderCell: (p) => <span className="d-block text-end text-primary fw-bold">{formatCurrency(p.commissions_due)}</span>,
    },
    {
      key: 'actions',
      label: '',
      width: 70,
      renderCell: (p) => (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onSelectMonth(periodKey(p)); }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      data={periods}
      columns={columns}
      getRowKey={(p) => periodKey(p)}
      onRowClick={(p) => onSelectMonth(periodKey(p))}
      isLoading={isLoading}
      preferencesKey="commission_periods_overview"
      userId={user?.id}
      userPreferences={userData?.preferences}
      maxHeight="100%"
      enableStickyHeader={true}
      enableColumnReorder={true}
      enableColumnResize={true}
      enableColumnVisibility={true}
      emptyState={<div className="text-center py-4 text-muted">No commission data for this period.</div>}
    />
  );
};

// (CheckIcon and buildInvoiceColumns moved into
// components/CommissionableInvoicesTable.tsx — that's the canonical
// Commissionable Sales table now, used by both this page and the new
// /sales view.)

// Single month commission detail view
const SINGLE_MONTH_FETCH_LIMIT = 50;

// SSE integration id the commissions engine recompute broadcasts under
// (mirrors commissions_v2.CommissionsEngineIntegrationID on the backend).
const COMMISSIONS_ENGINE_INTEGRATION_ID = 'commissions-engine';

const SingleMonthView: React.FC<{ userId: string; monthStart: string; monthEnd: string }> = ({ userId, monthStart, monthEnd }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();
  const [detailTab, setDetailTab] = useState<'invoices' | 'payouts'>('invoices');
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);

  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  const { data: periodsResp, isLoading: summaryLoading, isFetching: summaryFetching } = useGetCommissionsUserId(
    userId,
    { start_date: monthStart, end_date: monthEnd, sort_field: 'period_start' as any, sort_direction: 'desc' as any, limit: 1 },
    { query: { enabled: true, placeholderData: (prev: any) => prev } }
  );

  // Row identity. Per-user view returns one row per (invoice, role) for
  // this user — multiple rows can share inv.id when the user is both
  // salesperson and lead on the same invoice. Composite is shared with
  // the table's React keys via the getRowKey prop.
  const invoiceRowKey = useCallback(
    (r: CommissionableInvoiceRow) => r.attribution_id ?? '',
    []
  );

  // Sales Lead filter — case-insensitive LIKE search forwarded to the API.
  // Local-only (no URL persistence) since it's a quick filtering aid on
  // a drilled-in view, not a shareable bookmark target.
  const [salesLead, setSalesLead] = useState<string | undefined>(undefined);

  // Search / commission_status / role filters — also forwarded to the
  // API as cursor query params. Local-only state, same rationale as
  // salesLead above. Each one drives a fresh paginated fetch.
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [commissionStatus, setCommissionStatus] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);

  // Sort state — controlled, forwarded to the cursor on first-page
  // fetches. Local-only (no URL persistence), matching the other
  // filters on this drilled-in view. Default mirrors the cursor's
  // own fallback (booking_date desc).
  const [sortField, setSortField] = useState<string>('booking_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Refresh when a commissions-engine recompute COMPLETES. Toggling a line
  // delivered / an approval in the invoice modal enqueues an async recompute
  // that rewrites the shadow tables backing the per-row "due"/amount fields
  // and the period summary. The modal's own invalidate fires immediately —
  // ahead of that still-running recompute — so it reads stale "not due" rows.
  // The completion event (broadcast by PatchInvoicesId over SSE) is what tells
  // us the new state is actually queryable; refetch then so the row flips to
  // "due" without the operator navigating away and back. A ref-tracked set of
  // handled sync_ids keeps this firing once per recompute (completed events
  // linger in activeSyncs for a few seconds before cleanup).
  const { activeSyncs } = useNotifications();
  const handledRecomputesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const ev of activeSyncs.values()) {
      if (
        ev.integration_id === COMMISSIONS_ENGINE_INTEGRATION_ID &&
        ev.status === 'completed' &&
        ev.sync_id &&
        !handledRecomputesRef.current.has(ev.sync_id)
      ) {
        handledRecomputesRef.current.add(ev.sync_id);
        queryClient.invalidateQueries({ queryKey: [`commissions-user-invoices:${userId}`] });
        queryClient.invalidateQueries({ queryKey: [`/api/v1/commissions/${userId}`] });
      }
    }
  }, [activeSyncs, userId, queryClient]);

  // Cursor-paginated invoices. usePaginatedQuery pages first via params,
  // then via the cursor opaque blob. CommissionableInvoicesTable just
  // displays the accumulated rows + load-more button.
  const buildInvoicesParams = useCallback((cursor: string | undefined) => {
    if (cursor) return { cursor, limit: SINGLE_MONTH_FETCH_LIMIT };
    const base: any = {
      period_start: monthStart,
      period_end: monthEnd,
      sort_field: sortField,
      sort_direction: sortDirection,
      limit: SINGLE_MONTH_FETCH_LIMIT,
    };
    if (salesLead) base.sales_lead = salesLead;
    if (search) base.search = search;
    if (commissionStatus) base.commission_status = commissionStatus;
    if (role) base.role = role;
    return base;
  }, [monthStart, monthEnd, salesLead, search, commissionStatus, role, sortField, sortDirection]);

  const {
    allItems: invoices,
    apiSummary: invoicesSummary,
    totalCount: invoicesTotal,
    isLoading: invoicesLoading,
    hasMore: invoicesHasMore,
    handleLoadMore: invoicesLoadMore,
    error: invoicesError,
  } = usePaginatedQuery<CommissionableInvoiceRow, any, any>({
    queryKey: `commissions-user-invoices:${userId}`,
    buildQueryParams: buildInvoicesParams,
    queryFn: (params) => getCommissionsUserIdInvoices(userId, params as any),
    getRowKey: invoiceRowKey,
  });

  const summary = periodsResp?.data?.[0];
  const roles = summary?.roles || [];

  return (
    <>
      <h5 className="mb-3">Period {monthStart.slice(0, 7)}</h5>

      {summaryLoading && !summary ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : !summary ? (
        <Alert variant="info">No commission data found for this period.</Alert>
      ) : (
        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, opacity: summaryFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {/* Collapsible summary header */}
          <div
            className="d-flex align-items-center gap-2 mb-2"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setSummaryCollapsed(!summaryCollapsed)}
          >
            <i className={`bi ${summaryCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
            {summaryCollapsed && (() => {
              const totalQuota = roles.reduce((s, r) => s + parseFloat(r.quota_amount || '0'), 0);
              const totalNetRev = roles.reduce((s, r) => s + parseFloat(r.total_net_revenue || '0'), 0);
              const totalReceived = roles.reduce((s, r) => s + parseFloat(r.total_revenue_received || '0'), 0);
              const quotaPct = totalQuota > 0 ? (totalNetRev / totalQuota) * 100 : 0;
              const receivedPct = totalNetRev > 0 ? (totalReceived / totalNetRev) * 100 : 0;
              return (
                <span className="small">
                  <span className="text-muted">Quota:</span> <strong>{formatCurrency(totalQuota.toString())}</strong> <span className="text-muted">({quotaPct.toFixed(0)}%)</span>
                  <span className="text-muted ms-3">Net Rev:</span> <strong>{formatCurrency(totalNetRev.toString())}</strong>
                  <span className="text-muted ms-3">Received:</span> <strong>{formatCurrency(totalReceived.toString())}</strong> <span className="text-muted">({receivedPct.toFixed(0)}%)</span>
                  <span className="text-muted ms-3">Total Commission:</span> <strong>{formatCurrency(summary.total_commissions)}</strong>
                  <span className="text-muted ms-3">Paid:</span> <strong className="text-success">{formatCurrency(summary.commissions_paid)}</strong>
                  <span className="text-muted ms-3">Due:</span> <strong className="text-primary">{formatCurrency(summary.commissions_due)}</strong>
                </span>
              );
            })()}
          </div>

          {!summaryCollapsed && (
            <>
          {/* Totals */}
          <Row className="mb-3">
            <Col>
              <Card>
                <Card.Body className="d-flex justify-content-around text-center">
                  <div>
                    <div className="text-muted small">Total Commission</div>
                    <div className="fs-4 fw-bold">{formatCurrency(summary.total_commissions)}</div>
                  </div>
                  <div>
                    <div className="text-muted small">Commissions Paid</div>
                    <div className="fs-4 fw-bold text-success">{formatCurrency(summary.commissions_paid)}</div>
                  </div>
                  <div>
                    <div className="text-muted small">Commissions Due</div>
                    <div className="fs-4 fw-bold text-primary">{formatCurrency(summary.commissions_due)}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Per-Role Cards */}
          {roles.map((rs, i) => {
            const roleLabel = rs.role === 'sales_lead' ? 'Sales Lead' : 'Salesperson';
            const quota = parseFloat(rs.quota_amount || '0');
            const netRevenue = parseFloat(rs.total_net_revenue || '0');
            const received = parseFloat(rs.total_revenue_received || '0');
            const bookingPct = quota > 0 ? (netRevenue / quota) * 100 : 0;
            const receivedPct = netRevenue > 0 ? Math.min((received / netRevenue) * 100, 100) : 0;
            return (
              <div key={rs.role || i} className="mb-3">
                <h6 className="text-muted">{roleLabel}</h6>
                <Row className="mb-2">
                  <Col md={3}>
                    <Card><Card.Body>
                      <Card.Title className="fs-6 text-muted">Quota</Card.Title>
                      <div className="fs-3 fw-bold">{formatCurrency(rs.quota_amount)}</div>
                      <div className="small text-muted mt-1">Base Commission Rate: {formatPercent(rs.base_rate)}</div>
                      <div className="small text-muted">Over Quota Commission Rate: {formatPercent(rs.over_quota_rate)}</div>
                      <ProgressBar className="mt-2" now={bookingPct} variant={bookingPct >= 100 ? 'success' : bookingPct >= 75 ? 'info' : 'warning'} label={`${bookingPct.toFixed(0)}%`} />
                    </Card.Body></Card>
                  </Col>
                  <Col md={3}>
                    <Card><Card.Body>
                      <Card.Title className="fs-6 text-muted">Period Net Revenue</Card.Title>
                      <div className="fs-3 fw-bold">{formatCurrency(rs.total_net_revenue)}</div>
                      <Table borderless size="sm" className="mt-2 mb-0 small"><tbody>
                        <tr><td className="text-muted">Base Revenue</td><td className="text-end">{formatCurrency(rs.base_revenue)}</td></tr>
                        <tr><td className="text-muted">Over Quota Revenue</td><td className="text-end">{formatCurrency(rs.over_quota_revenue)}</td></tr>
                      </tbody></Table>
                    </Card.Body></Card>
                  </Col>
                  <Col md={3}>
                    <Card><Card.Body>
                      <Card.Title className="fs-6 text-muted">Total Revenue Received</Card.Title>
                      <div className="fs-3 fw-bold">{formatCurrency(rs.total_revenue_received)}</div>
                      <ProgressBar className="mt-2" now={receivedPct} variant={receivedPct >= 100 ? 'success' : receivedPct >= 75 ? 'info' : 'warning'} label={`${receivedPct.toFixed(0)}%`} />
                      <Table borderless size="sm" className="mt-2 mb-0 small"><tbody>
                        <tr><td className="text-muted">Base Revenue Received</td><td className="text-end">{formatCurrency(rs.base_revenue_received)}</td></tr>
                        <tr><td className="text-muted">Over Quota Revenue Received</td><td className="text-end">{formatCurrency(rs.over_quota_revenue_received)}</td></tr>
                      </tbody></Table>
                    </Card.Body></Card>
                  </Col>
                  <Col md={3}>
                    <Card><Card.Body>
                      <Card.Title className="fs-6 text-muted">Total Commission</Card.Title>
                      <div className="fs-3 fw-bold">{formatCurrency(rs.total_commissions)}</div>
                      <Table borderless size="sm" className="mt-2 mb-0 small"><tbody>
                        <tr><td className="text-muted">Base Commissions</td><td className="text-end">{formatCurrency(rs.base_commissions)}</td></tr>
                        <tr><td className="text-muted">Over Quota Commissions</td><td className="text-end">{formatCurrency(rs.over_quota_commissions)}</td></tr>
                        <tr className="border-top"><td className="text-muted fw-semibold">Approved</td><td className="text-end fw-semibold">{formatCurrency(rs.approved_commissions)}</td></tr>
                        <tr><td className="text-muted">Commissions Paid</td><td className="text-end">{formatCurrency(rs.commissions_paid)}</td></tr>
                        <tr><td className="fw-bold">Commissions Due</td><td className="text-end fw-bold text-primary">{formatCurrency(rs.commissions_due)}</td></tr>
                        {parseFloat(rs.commissions_prepaid || '0') > 0 && (
                          <tr><td className="fw-bold">Commissions Prepaid</td><td className="text-end fw-bold text-warning">{formatCurrency(rs.commissions_prepaid)}</td></tr>
                        )}
                        {parseFloat(rs.commissions_missing_quota || '0') > 0 && (
                          <tr><td className="fw-bold">Paid w/o Quota</td><td className="text-end fw-bold text-danger" title="Commission paid on rows whose booking_date isn't covered by any configured quota for this user+role">{formatCurrency(rs.commissions_missing_quota)}</td></tr>
                        )}
                      </tbody></Table>
                    </Card.Body></Card>
                  </Col>
                </Row>
              </div>
            );
          })}
            </>
          )}

          {/* Detail tabs.
              minHeight floors the wrapper at: Card.minHeight (240px) + Nav (~42px)
              + pb-3 (16px) + slack — so the active tab-pane never shrinks below
              what the Card needs, and the page-level scroll (DashboardLayout
              scrollable) takes over for tighter viewports. */}
          <div className="flex-grow-1 d-flex flex-column pb-3" style={{ minHeight: '500px' }}>
          <Tabs activeKey={detailTab} onSelect={(k) => k && setDetailTab(k as any)} mountOnEnter unmountOnExit>
            <Tab eventKey="invoices" title="Commissionable Sales">
              <CommissionableInvoicesTable
                invoices={invoices}
                isLoading={invoicesLoading}
                error={invoicesError}
                preferencesKey="commission_invoices"
                userId={user?.id}
                userPreferences={userData?.preferences}
                totalCount={invoicesTotal ?? invoicesSummary?.row_count ?? undefined}
                pagination={{ hasMore: invoicesHasMore, limit: SINGLE_MONTH_FETCH_LIMIT }}
                onLoadMore={invoicesLoadMore}
                getRowKey={invoiceRowKey}
                salesLead={salesLead}
                onSalesLeadChange={setSalesLead}
                search={search}
                onSearchChange={setSearch}
                commissionStatus={commissionStatus}
                onCommissionStatusChange={setCommissionStatus}
                role={role}
                onRoleChange={setRole}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={(field, dir) => { setSortField(field); setSortDirection(dir); }}
                /* No onInvoiceUpdated refresh here: a delivery/approval toggle
                   enqueues an ASYNC commissions recompute, so refetching now
                   would fire a duplicate query that races — and loses to — that
                   recompute, reading stale "not due" rows. The commissions-engine
                   SSE 'completed' effect above issues the single correct refetch
                   once the shadow tables have actually been rewritten. */
              />
            </Tab>
            <Tab eventKey="payouts" title="Commission Payments">
              <CommissionPaymentsTable
                scope="user"
                userId={userId}
                dateRange={{ start_date: monthStart, end_date: monthEnd }}
                preferencesKey="commission_payments_user"
                currentUserId={user?.id}
                userPreferences={userData?.preferences}
                lockBucketBy="booking_date"
              />
            </Tab>
          </Tabs>
          </div>
        </div>
      )}

    </>
  );
};

// ============================================================================
// Stranded bank transactions — counterparties whose commission BTs match the
// derive prefilter but have no linked Payee, so no CommissionPayment is being
// produced. Aggregated by (src_type, src_id, counterparty_id) on the server.
// One actionable row per stranded payee link; the inline user dropdown drives
// PUT /payees/{id}/user, which (re-)derives the CPs in the same transaction.
// ============================================================================

const StrandedBankTransactionsSection: React.FC<{ users: User[]; canEdit: boolean }> = ({ users, canEdit }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetCommissionsStrandedBankTransactions();
  const rows: StrandedCounterparty[] = (data as any)?.data || [];

  // Per-row pending state. Key = payee_id when present, else
  // `${src_type}:${src_id}:${counterparty_id}` so rows without a Payee
  // still get a stable key.
  const rowKey = (r: StrandedCounterparty) =>
    r.payee_id ? `payee:${r.payee_id}` : `bt:${r.src_type || ''}:${r.src_id || ''}:${r.counterparty_id || ''}`;

  const [pendingByRow, setPendingByRow] = useState<Record<string, string>>({});
  const [savingRow, setSavingRow] = useState<string | null>(null);
  const [errorByRow, setErrorByRow] = useState<Record<string, string>>({});

  const activeUsers = useMemo(() => users.filter(u => u.active !== false), [users]);

  // Options for the per-row link dropdown (react-select format). The
  // empty-string entry isn't included here because the action is
  // "pick a user to link"; clearing/unlinking from this surface is
  // handled by the FBAI tab. Built once per user-list change so every
  // row reuses the same array reference.
  const userOptions = useMemo(() => activeUsers.map(u => ({
    value: u.id || '',
    label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id || '',
  })), [activeUsers]);

  // handleLink takes the chosen userId explicitly (rather than reading
  // from pendingByRow via closure) because renderCell's call site
  // hands us a stable function reference that DataTable captured at
  // first render — closures over component state would always read
  // stale values. Same reasoning for activeUsers / savingRow / errors:
  // we inject them into the row data below.
  const handleLink = async (r: StrandedCounterparty, userId: string) => {
    const key = rowKey(r);
    if (!userId || !r.payee_id) return;
    setSavingRow(key);
    setErrorByRow(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      await putPayeesIdUser(r.payee_id, { user_id: userId });
      // Linking re-derives CPs server-side in the same transaction, so
      // the issues + payments lists both want a refresh. Stranded list
      // also drops this row (counterparty is no longer stranded).
      queryClient.invalidateQueries({ queryKey: getGetCommissionsStrandedBankTransactionsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetCommissionPaymentsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['commission-payments'] });
      setPendingByRow(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err: any) {
      setErrorByRow(prev => ({
        ...prev,
        [key]: err?.response?.data?.message || err?.message || 'Failed to link payee',
      }));
    } finally {
      setSavingRow(null);
    }
  };

  if (isLoading) {
    return (
      <div>
        <h5 className="mb-2">
          <i className="bi bi-link-45deg text-warning me-2" />
          Stranded bank transactions
        </h5>
        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
      </div>
    );
  }
  if (rows.length === 0) return null;

  return (
    <div>
      <h5 className="mb-2">
        <i className="bi bi-link-45deg text-warning me-2" />
        Stranded bank transactions
        <Badge bg="warning" text="dark" className="ms-2">{rows.length}</Badge>
      </h5>
      <p className="text-muted small mb-2">
        Counterparties receiving "ASG Commission" payments whose payee isn't linked
        to a dashboard user. Pick a user and click Link to materialize the
        CommissionPayments. Mercury counterparties have a payee row already; file-
        source counterparties need a fresh upload first so syncFileAccountPayees
        can create the payee row.
      </p>
      <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: '200px' }}>
        <DataTable
          data={rows.map(r => {
            const k = rowKey(r);
            return {
              ...r,
              _key: k,
              _err: errorByRow[k],
              _pending: pendingByRow[k] || '',
              _isSaving: savingRow === k,
              // userOptions must travel via the row, not via closure —
              // DataTable captures columns once, so renderCell would
              // otherwise read the options array from the first render
              // (when useAllUsers is still empty) and stay stale forever.
              _userOptions: userOptions,
              // canEdit also travels via the row so the renderCell's
              // gate doesn't get frozen at first render.
              _canEdit: canEdit,
            };
          })}
          columns={[
            {
              key: 'counterparty_name', label: 'Counterparty', width: 220, minWidth: 160, sortable: true,
              renderCell: (r: any) => r.counterparty_name || r.counterparty_id || '—',
            },
            {
              key: 'src_type', label: 'Source', width: 110, minWidth: 90, sortable: true,
              renderCell: (r: any) => (
                <Badge bg={r.src_type === 'mercury' ? 'info' : 'secondary'}>
                  {r.src_type === 'mercury' ? 'Mercury' : r.src_type === 'file' ? 'File' : (r.src_type || '—')}
                </Badge>
              ),
            },
            {
              key: 'bt_count', label: 'BTs', width: 80, minWidth: 60, sortable: true,
              renderCell: (r: any) => <span className="d-block text-end">{r.bt_count ?? 0}</span>,
              sortValue: (r: any) => r.bt_count ?? 0,
            },
            {
              key: 'total_amount', label: 'Total', width: 130, minWidth: 100, sortable: true,
              renderCell: (r: any) => <span className="d-block text-end">{formatCurrency(r.total_amount)}</span>,
              sortValue: (r: any) => parseFloat(r.total_amount || '0'),
            },
            {
              key: 'date_range', label: 'Date range', width: 200, minWidth: 160, sortable: false,
              renderCell: (r: any) => (
                r.earliest_date && r.latest_date
                  ? <span>{formatLocalDate(r.earliest_date)} → {formatLocalDate(r.latest_date)}</span>
                  : '—'
              ),
            },
            {
              key: 'action', label: 'Link to user', width: 320, minWidth: 260, sortable: false,
              renderCell: (r: any) => {
                // Read everything off the row — DataTable's column-once
                // capture means closures over component state would be
                // frozen at first render. Setters and handleLink are
                // safe via closure: the setters are stable refs from
                // useState, and handleLink takes userId as an arg
                // rather than reading it from state internally.
                const key = r._key as string;
                const isSaving = !!r._isSaving;
                const noPayee = !r.payee_id;
                const pending = r._pending || '';
                const rowOptions = (r._userOptions || []) as Array<{ value: string; label: string }>;
                const selected = rowOptions.find(o => o.value === pending) || null;
                const canEditRow = !!r._canEdit;
                // Read-only branch — VP/sales_manager can see the
                // stranded row but can't act on it. Surface the
                // current state ("unlinked" / payee name / orphan)
                // so they understand what's pending without exposing
                // a writable control they can't actually submit.
                if (!canEditRow) {
                  return (
                    <span className="text-muted small">
                      <i className="bi bi-lock me-1" />
                      Linkable by admin / finance only
                    </span>
                  );
                }
                return (
                  <div className="d-flex flex-column gap-1">
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <Select
                        options={rowOptions}
                        value={selected}
                        onChange={(opt: any) => {
                          const v = (opt && opt.value) || '';
                          setPendingByRow(prev => {
                            const next = { ...prev };
                            if (v) next[key] = v; else delete next[key];
                            return next;
                          });
                        }}
                        isDisabled={noPayee || isSaving}
                        placeholder={noPayee ? 'No payee yet — re-upload first' : 'Pick user…'}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                          // Pin the container so neighboring elements
                          // (Save/Link button, error message) can't
                          // shift the control's position via flex.
                          container: (base: any) => ({ ...base, flex: '0 0 auto' }),
                        }}
                      />
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={noPayee || isSaving || !pending}
                        onClick={() => handleLink(r, pending)}
                      >
                        {isSaving ? (<><Spinner size="sm" animation="border" className="me-1" />…</>) : 'Link'}
                      </Button>
                      {pending && !isSaving && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setPendingByRow(prev => {
                            if (!(key in prev)) return prev;
                            const next = { ...prev };
                            delete next[key];
                            return next;
                          })}
                          title="Discard the staged user selection"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                    {r._err && (
                      <div className="text-danger small">
                        <i className="bi bi-exclamation-triangle-fill me-1" />
                        {r._err}
                      </div>
                    )}
                  </div>
                );
              },
            },
          ] as ColumnConfig<any>[]}
          getRowKey={(r: any) => r._key}
          enableColumnReorder={false}
          enableColumnResize={true}
          enableColumnVisibility={false}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Issues view — surfaces commission_payments whose parser flagged a problem
// (validation_errors set). Per-user scoping: salespeople see their own;
// admins/managers see everything. No date filter — we want every open issue
// regardless of when it was paid.
// ============================================================================

const ISSUES_DATE_RANGE = { start_date: '2000-01-01', end_date: '2099-12-31' };

const IssuesView: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const { data: usersData } = useAllUsers();
  const allUsers: User[] = useMemo(() => usersData || [], [usersData]);

  // Wait until auth is fully hydrated before deciding scope. Without this
  // guard, the first render (user=null/role=undefined) falls through to the
  // salesperson branch with userId=undefined, react-query caches the failed
  // call under a key that subsequent renders don't invalidate, and the table
  // appears empty even after auth loads.
  if (!user || !user.id || !user.role) {
    return null;
  }

  const isManagerOrAdmin = user.role === 'admin' || user.role === 'finance'
    || user.role === 'vp_sales' || user.role === 'sales_manager';
  // Mutating linkage (PUT /payees/{id}/user) is gated to admin +
  // finance on the backend. VP / sales_manager can see issues but
  // can't act on them — the inline editor is hidden from them.
  const canEditPayeeLinks = user.role === 'admin' || user.role === 'finance';

  return (
    // flex-grow-1 + minHeight:0 propagates the available viewport
    // height down to the CommissionPaymentsTable below; without these
    // the inner Card's `flex-grow-1` + `overflow: hidden` can't
    // expand and the table renders at its natural (small) content
    // height. gap-4 stays for the visual breathing-room between
    // sections. flex-shrink-0 on the static sections + flex-grow-1
    // on the Issues table is what lets the table fill remaining space.
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, gap: '1.5rem' }}>
      {/* Stranded bank transactions — admin/manager only. Surfaces
          counterparties whose commission BTs aren't deriving CPs
          because no linked payee points the counterparty at a user.
          Action is link-the-payee, not edit-each-BT, so rows are
          aggregated by counterparty. */}
      {isManagerOrAdmin && (
        <div className="flex-shrink-0">
          <StrandedBankTransactionsSection users={allUsers} canEdit={canEditPayeeLinks} />
        </div>
      )}

      {/* Header for the issues table — payment-centric framing now that
          validation lives on commission_payment_invoices, not on invoices.
          Admin/manager only; the description and reconciliation guidance
          targets people with QB edit access. The table below carries its
          own count and action buttons, so no separate count/empty-state
          alert here. */}
      {isManagerOrAdmin && (
        <div className="flex-shrink-0">
          <h5 className="mb-2">
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            Commission Payment attribution issues
          </h5>
          <p className="text-muted small mb-0">
            Commission payments whose claimed role/recipient conflicts with the
            linked invoice's attribution data. Reconcile by fixing the source
            memo (use Note to override external_memo on bank transactions) or
            the invoice's role attribution in QuickBooks (Sales Person/Lead
            custom field, line items, or PrivateNote attribution lines).
          </p>
        </div>
      )}

      {/* Issues table. Force paid_at bucketing so linkless rows with errors
          still surface — booking_date mode would silently exclude them.
          The table itself owns flex-grow-1 internally; we wrap it in a
          flex-grow-1 + minHeight:0 div so it can actually fill the space
          our outer flex column provides. */}
      <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      {isManagerOrAdmin ? (
        <CommissionPaymentsTable
          scope="admin"
          dateRange={ISSUES_DATE_RANGE}
          urlPathname="/commissions/issues"
          preferencesKey="commission_issues"
          currentUserId={user.id}
          userPreferences={userData?.preferences}
          allUsers={allUsers}
          validationErrorsOnly
          lockBucketBy="paid_at"
        />
      ) : (
        <CommissionPaymentsTable
          scope="user"
          userId={user.id}
          dateRange={ISSUES_DATE_RANGE}
          urlPathname="/commissions/issues"
          preferencesKey="commission_issues_user"
          currentUserId={user.id}
          userPreferences={userData?.preferences}
          validationErrorsOnly
          lockBucketBy="paid_at"
        />
      )}
      </div>
    </div>
  );
};

// ============================================================================
// Payments view — admin tab; owns date range/period + URL sync, delegates the
// table/filters/selection/modals to the shared <CommissionPaymentsTable>.
// ============================================================================

const PaymentsView: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const { data: usersData } = useAllUsers();
  const allUsers: User[] = useMemo(() => usersData || [], [usersData]);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Date range/period state — persisted to URL for the admin tab.
  const [period, setPeriod] = useState<string>(() => searchParams.get('period') || 'month');
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string }>(() => {
    const urlStart = searchParams.get('start_date');
    const urlEnd = searchParams.get('end_date');
    if (urlStart && urlEnd) return { start_date: urlStart, end_date: urlEnd };
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    };
  });

  // Sync period/dateRange to URL — guarded on pathname so an outgoing view
  // can't re-write during a tab switch. Filters/sort are synced inside the
  // shared table component.
  useEffect(() => {
    if (location.pathname !== '/commissions/payments') return;
    const params = new URLSearchParams(searchParams);
    params.set('period', period);
    if (dateRange.start_date) params.set('start_date', dateRange.start_date);
    if (dateRange.end_date) params.set('end_date', dateRange.end_date);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, period, location.pathname]);

  const handlePeriodChange = useCallback((event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode);
    setDateRange({
      start_date: event.dateRange.start_date.split('T')[0],
      end_date: event.dateRange.end_date.split('T')[0],
    });
  }, []);

  return (
    <CommissionPaymentsTable
      scope="admin"
      dateRange={dateRange}
      period={period}
      onPeriodChange={handlePeriodChange}
      urlPathname="/commissions/payments"
      preferencesKey="commission_payments"
      currentUserId={user?.id}
      userPreferences={userData?.preferences}
      allUsers={allUsers}
    />
  );
};

// ============================================================================
// Team view — team-wide commissionable-invoice aggregate by default, with a
// drilldown picker that switches to the single-user CommissionView. Backend
// 'team' scope already filters sales_manager → own reports / admin → all.
// ============================================================================

const TEAM_FETCH_LIMIT = 50;
const TEAM_PATH = '/commissions/team';

// salesNamesFor returns the deduped, role-filtered list of attribution
// names from inv.attributions, with scalar fallback. Powers the two
// extra columns on the team-aggregate table.
function salesNamesFor(inv: Invoice, role: 'salesperson' | 'sales_lead'): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of inv.attributions || []) {
    if (a.role !== role) continue;
    const n = (a.name || '').trim();
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  if (out.length === 0) {
    const scalar = role === 'salesperson' ? inv.sales_person : inv.sales_lead;
    if (scalar && scalar.trim()) out.push(scalar.trim());
  }
  return out;
}

interface SummaryCardProps {
  icon: string;
  tone: 'primary' | 'success' | 'warning' | 'info' | 'danger';
  value: string;
  label: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon, tone, value, label }) => (
  <Card style={{ minWidth: 200, flex: '1 1 200px' }}>
    <Card.Body className="text-center p-2">
      <div className="mb-2"><i className={`bi ${icon} fs-1 text-${tone}`}></i></div>
      <h3 className="mb-1">{value}</h3>
      <p className="text-muted mb-0 small">{label}</p>
    </Card.Body>
  </Card>
);

interface TeamMemberOption {
  value: string;
  label: string;
  email: string;
  role: string;
}

const TeamView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { data: usersData } = useAllUsers();
  const allUsers: User[] = usersData || [];
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const [selectedUserId, setSelectedUserId] = useState<string>(searchParams.get('user') || '');
  // Projection picker state. When non-empty, the view reads from the
  // projection's invoices endpoint (joined with live for show_mode).
  // URL-persisted as ?projection=<id> so refresh/share preserves it.
  const [selectedProjectionId, setSelectedProjectionId] = useState<string | undefined>(
    searchParams.get('projection') || undefined,
  );
  // Modal-open state for the projection params editor. Triggered by the
  // gear icon next to the picker (only visible when a projection is
  // selected). Not URL-persisted — purely transient UI.
  const [showProjectionForm, setShowProjectionForm] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'finance' || user?.role === 'vp_sales';

  const getReports = (managerId: string): User[] => {
    const direct = allUsers.filter(u => u.manager_id === managerId && u.active !== false);
    const all: User[] = [];
    for (const r of direct) {
      all.push(r);
      if (r.id) all.push(...getReports(r.id));
    }
    return all;
  };

  const reports = isAdmin
    ? allUsers.filter(u => u.active !== false)
    : user?.id ? getReports(user.id) : [];

  const sortedReports = [...reports].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
  );

  // Synthetic "Team" entry pinned at the top represents the aggregate
  // view. Its value is '' so an empty selectedUserId resolves back to it
  // (i.e. clearing or first-load with no ?user= shows Team selected, not
  // an empty placeholder).
  const TEAM_OPTION: TeamMemberOption = { value: '', label: 'Team', email: '', role: '' };
  const teamOptions: TeamMemberOption[] = [
    TEAM_OPTION,
    ...sortedReports
      .filter(r => r.id)
      .map(r => ({
        value: r.id!,
        label: `${r.first_name} ${r.last_name}`,
        email: r.email || '',
        role: r.role || '',
      })),
  ];

  const selectedOption = teamOptions.find(o => o.value === selectedUserId) || TEAM_OPTION;

  // Aggregate-mode state. URL-persisted so refresh/share keeps period
  // + filters + sort.
  const [initialDateParams] = useState(() => parseInitialDateParams(searchParams));
  const [period, setPeriod] = useState<Period>(initialDateParams.period);
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string }>({
    start_date: initialDateParams.start_date,
    end_date: initialDateParams.end_date,
  });
  const [salesperson, setSalesperson] = useState<string | undefined>(searchParams.get('salesperson') || undefined);
  const [salesLead, setSalesLead] = useState<string | undefined>(searchParams.get('sales_lead') || undefined);
  const [search, setSearch] = useState<string | undefined>(searchParams.get('search') || undefined);
  // ?? undefined preserves '' as "match nothing" (deselect-all) across refresh.
  const [commissionStatus, setCommissionStatus] = useState<string | undefined>(searchParams.get('commission_status') ?? undefined);
  // Team-view role is required + single-valued (backend enforces this on team
  // scope). Default to 'salesperson' if the URL has no ?role= or carries an
  // unsupported value. Use plain string (not undefined) so the table's
  // required-single-select always has a current value.
  const [role, setRole] = useState<string>(() => {
    const raw = searchParams.get('role');
    return raw === 'salesperson' || raw === 'sales_lead' ? raw : 'salesperson';
  });
  const [sortField, setSortField] = useState<string>(searchParams.get('sort_field') || 'booking_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    searchParams.get('sort_direction') === 'asc' ? 'asc' : 'desc'
  );

  // Single URL-sync effect for all team-view params (selectedUser + aggregate
  // filters/sort/period). Guarded on pathname so this tab can't clobber
  // sibling-tab URL state during a switch.
  useEffect(() => {
    if (location.pathname !== TEAM_PATH) return;
    const next = new URLSearchParams(searchParams);
    if (selectedUserId) next.set('user', selectedUserId); else next.delete('user');
    if (selectedProjectionId) next.set('projection', selectedProjectionId); else next.delete('projection');
    next.set('period', period);
    if (dateRange.start_date) next.set('start_date', dateRange.start_date); else next.delete('start_date');
    if (dateRange.end_date) next.set('end_date', dateRange.end_date); else next.delete('end_date');
    if (salesperson) next.set('salesperson', salesperson); else next.delete('salesperson');
    if (salesLead) next.set('sales_lead', salesLead); else next.delete('sales_lead');
    if (search) next.set('search', search); else next.delete('search');
    if (commissionStatus !== undefined) next.set('commission_status', commissionStatus); else next.delete('commission_status');
    next.set('role', role);
    if (sortField !== 'booking_date') next.set('sort_field', sortField); else next.delete('sort_field');
    if (sortDirection !== 'desc') next.set('sort_direction', sortDirection); else next.delete('sort_direction');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, selectedProjectionId, period, dateRange, salesperson, salesLead, search, commissionStatus, role, sortField, sortDirection, location.pathname]);

  const handlePeriodChange = useCallback((event: PeriodChangeEvent) => {
    const nextStart = event.dateRange.start_date ? event.dateRange.start_date.split('T')[0] : '';
    const nextEnd = event.dateRange.end_date ? event.dateRange.end_date.split('T')[0] : '';
    setPeriod(prev => prev === (event.navigationMode as Period) ? prev : (event.navigationMode as Period));
    setDateRange(prev => {
      if (prev.start_date === nextStart && prev.end_date === nextEnd) return prev;
      return { start_date: nextStart, end_date: nextEnd };
    });
  }, []);

  // Row identity. 'team' scope returns one row per (invoice, role,
  // attribution_name); attribution_id is the unique row key.
  const getRowKey = useCallback(
    (r: CommissionableInvoiceRow) => r.attribution_id ?? '',
    []
  );

  const buildQueryParams = useCallback((cursor: string | undefined) => {
    if (cursor) return { cursor, limit: TEAM_FETCH_LIMIT };
    const base: any = {
      sort_field: sortField,
      sort_direction: sortDirection,
      limit: TEAM_FETCH_LIMIT,
    };
    if (dateRange.start_date) base.period_start = dateRange.start_date;
    if (dateRange.end_date) base.period_end = dateRange.end_date;
    if (salesperson) base.salesperson = salesperson;
    if (salesLead) base.sales_lead = salesLead;
    if (search) base.search = search;
    if (commissionStatus !== undefined) base.commission_status = commissionStatus;
    if (role !== undefined) base.role = role;
    if (selectedProjectionId) base.projection_id = selectedProjectionId;
    return base;
  }, [dateRange, salesperson, salesLead, search, commissionStatus, role, sortField, sortDirection, selectedProjectionId]);

  // queryFn is always the team-aggregate endpoint; projection mode is
  // signalled via the projection_id query param (cursor swaps the
  // enrichment data source + applies show_mode).
  const queryFn = useCallback((params: Record<string, any>) => {
    return getCommissionsUserIdInvoices('team', params as any);
  }, []);

  const inAggregateMode = !selectedUserId;
  // Distinct queryKey per projection (and the live team aggregate) so
  // react-query caches don't collide. usePaginatedQuery uses the key
  // as a prefix; switching projections triggers a fresh fetch.
  const queryKey = selectedProjectionId
    ? `projection-invoices-${selectedProjectionId}`
    : 'team-aggregate-invoices';

  const {
    allItems: invoices,
    apiSummary,
    totalCount,
    isLoading,
    hasMore,
    handleLoadMore,
    reset: resetInvoices,
    error: paginationError,
  } = usePaginatedQuery<CommissionableInvoiceRow, any, CommissionableInvoicesSummary>({
    queryKey,
    buildQueryParams,
    queryFn,
    getRowKey,
    enabled: inAggregateMode,
  });

  const summary: CommissionableInvoicesSummary | undefined = apiSummary;

  // Refresh when a commissions-engine recompute COMPLETES — same rationale as
  // SingleMonthView. A delivery/approval toggle in the invoice modal (or a
  // projection recompute) rewrites the shadow tables backing the per-row
  // "due"/amount fields and the summary; refetch on the SSE 'completed' event
  // rather than immediately on the PATCH, which would race the recompute.
  // Invalidates the CURRENT queryKey, so the live-team aggregate and a selected
  // projection each refresh off their own engine's completion. Handled-id set
  // keeps it firing once per recompute (completed events linger ~5s).
  const { activeSyncs } = useNotifications();
  const handledRecomputesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const ev of activeSyncs.values()) {
      if (
        ev.integration_id === COMMISSIONS_ENGINE_INTEGRATION_ID &&
        ev.status === 'completed' &&
        ev.sync_id &&
        !handledRecomputesRef.current.has(ev.sync_id)
      ) {
        handledRecomputesRef.current.add(ev.sync_id);
        // reset(), not invalidateQueries. The visible list is an accumulation
        // of one cached query per page, and only the page currently being
        // observed has a live subscriber — so invalidating the prefix refetched
        // just that page (which the accumulator then appended a second time)
        // and left every earlier page showing pre-recompute values. reset()
        // drops the cached pages, clears the accumulator and re-fetches from
        // page 1, which is what "the data changed, reload the list" means here.
        resetInvoices();
      }
    }
  }, [activeSyncs, resetInvoices]);

  // Extra columns show the FULL attribution roster on each row so a
  // multi-attribution invoice's rows both display both names. The
  // backend sortRows whitelists salesperson_names / sales_lead_names
  // and uses the alphabetically-first matching attribution as the
  // sort key.
  const extraColumns: ColumnConfig<CommissionableInvoiceRow>[] = useMemo(() => [
    {
      key: 'salesperson_names',
      label: 'Salesperson',
      width: 140,
      minWidth: 110,
      sortable: true,
      renderCell: (r) => {
        if (!r.invoice) return null;
        const names = salesNamesFor(r.invoice, 'salesperson');
        if (names.length === 0) return null;
        return <span title={names.join(', ')}>{names.join(', ')}</span>;
      },
    },
    {
      key: 'sales_lead_names',
      label: 'Sales Lead',
      width: 140,
      minWidth: 110,
      sortable: true,
      renderCell: (r) => {
        if (!r.invoice) return null;
        const names = salesNamesFor(r.invoice, 'sales_lead');
        if (names.length === 0) return null;
        return <span title={names.join(', ')}>{names.join(', ')}</span>;
      },
    },
  ], []);

  if (reports.length === 0) {
    return <Alert variant="info">No team members found.</Alert>;
  }

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'auto' }}>
      {selectedProjectionId && (
        <ProjectionFormPanel
          selectedId={selectedProjectionId}
          show={showProjectionForm}
          onClose={() => setShowProjectionForm(false)}
          onDeleted={() => { setShowProjectionForm(false); setSelectedProjectionId(undefined); }}
        />
      )}
      <Select<TeamMemberOption>
        options={teamOptions}
        value={selectedOption}
        onChange={(opt) => setSelectedUserId(opt?.value || '')}
        // Drop the focus ring after a pick. Defaults to false on desktop
        // in react-select, so the picker would otherwise stay focused
        // and look "stuck" until the next outside click.
        blurInputOnSelect
        // Show the X only when an individual is picked; when Team is
        // selected there's nothing to clear back to.
        isClearable={selectedUserId !== ''}
        formatOptionLabel={(opt) => {
          if (opt.value === '') {
            return (
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-people-fill text-primary"></i>
                <span className="fw-medium">Team</span>
              </div>
            );
          }
          return (
            <div className="d-flex align-items-center gap-2">
              <span className="fw-medium">{opt.label}</span>
              <span className="text-muted small">{opt.email}</span>
              <Badge bg="secondary">{opt.role}</Badge>
            </div>
          );
        }}
        className="mb-3"
      />

      {selectedUserId ? (
        <CommissionView userId={selectedUserId} />
      ) : (
        <>
          <div className="d-flex gap-3 align-items-stretch flex-wrap mb-3">
            <SummaryCard icon="bi-cash-stack" tone="success"
              value={formatCurrency(summary?.net_booked_revenue)} label="Net Booked Revenue" />
            <SummaryCard icon="bi-bank" tone="primary"
              value={formatCurrency(summary?.total_payments_received)} label="Total Payments Received" />
            <SummaryCard icon="bi-check2-circle" tone="success"
              value={formatCurrency(summary?.commissions_paid)} label="Commissions Paid" />
            <SummaryCard icon="bi-hourglass-split" tone="warning"
              value={formatCurrency(summary?.commissions_due)} label="Commissions Due" />
          </div>
          <CommissionableInvoicesTable
            invoices={invoices}
            isLoading={isLoading}
            error={paginationError}
            preferencesKey="team_aggregate_invoices"
            userId={user?.id}
            userPreferences={userData?.preferences}
            extraColumns={extraColumns}
            invoiceQueryKey={['invoice']}
            totalCount={totalCount ?? summary?.row_count ?? undefined}
            pagination={{ hasMore, limit: TEAM_FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            getRowKey={getRowKey}
            title="Team commissions for"
            titleIcon="bi-graph-up-arrow"
            headerExtra={
              <div className="d-flex align-items-center gap-1">
                <ProjectionPicker
                  selectedId={selectedProjectionId}
                  onSelect={setSelectedProjectionId}
                />
                {selectedProjectionId && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    title="Edit projection params"
                    onClick={() => setShowProjectionForm(true)}
                  >
                    <i className="bi bi-gear"></i>
                  </Button>
                )}
              </div>
            }
            showDateNav
            initialDateRange={{ start_date: initialDateParams.start_date, end_date: initialDateParams.end_date }}
            initialPeriod={initialDateParams.period}
            onPeriodChange={handlePeriodChange}
            salesperson={salesperson}
            salesLead={salesLead}
            onSalespersonChange={setSalesperson}
            onSalesLeadChange={setSalesLead}
            search={search}
            onSearchChange={setSearch}
            commissionStatus={commissionStatus}
            onCommissionStatusChange={setCommissionStatus}
            role={role}
            // 'Clear all filters' fires onRoleChange(undefined); collapse
            // that to 'salesperson' so the required single-select always
            // has a value and the backend never sees a blank team-scope
            // role. Stale ?role= values were normalised at mount.
            onRoleChange={(v) => setRole(v === 'salesperson' || v === 'sales_lead' ? v : 'salesperson')}
            roleFilterMode="single-required"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={(field, dir) => { setSortField(field); setSortDirection(dir); }}
            /* No onInvoiceUpdated refresh here: a delivery/approval toggle
               enqueues an ASYNC commissions recompute, so refetching now would
               fire a duplicate query that races — and loses to — that recompute.
               The commissions-engine SSE 'completed' effect issues the single
               correct refetch once the shadow tables have been rewritten. */
          />
        </>
      )}
    </div>
  );
};

// ============================================================================
// Config view — quota management + import
// ============================================================================

// Parsed person from legacy config file
// id is present in the import file (the dashboard upload requires it
// per quota row so re-imports upsert instead of duplicating). Stored
// as part of the per-quota tuple so we can forward it on submit and
// surface "missing id" rows in the mapping UI.
interface ImportPerson {
  configName: string;
  role: 'salesperson' | 'sales_lead';
  quotas: { id?: string; start: string; end: string; repeat: string; quota: number; baseRate: number; overQuotaRate: number }[];
}

// buildConflictMessage formats one 409 conflict into a multi-line
// string for the Errors column. Names the user instead of dumping a
// UUID, and lists every field in which the upload's value differs
// from the live row's value so the operator can decide which side
// is correct.
//
// The message itself is the conflict header line plus, for each
// differing field, a "field: upload X, live Y" line. The role and
// the period fields are special-cased to friendly labels and dates;
// quota / rates are formatted with the same helpers used elsewhere.
function buildConflictMessage(
  upload: QuotaConfigUpload,
  c: SalesQuotaLinkConflict,
  userNameByID: Map<string, string>
): string {
  const e = c.existing;
  if (!e) return 'Mapping rejected — no existing-row detail provided';
  const userLabel = e.user_id ? (userNameByID.get(e.user_id) || `user ${e.user_id}`) : 'unknown user';
  const roleLabel = (r?: string) => r === 'sales_lead' ? 'Sales Lead' : 'Salesperson';
  const dateLabel = (d?: any) => {
    if (!d) return '?';
    if (typeof d === 'string') return d;
    return String(d);
  };
  // Side labels differ by conflict kind: existing_live's other side
  // is a real live row in sales_quotas; in_batch's other side is
  // another upload from this same Save attempt.
  const inBatch = c.kind === 'in_batch';
  const thisLabel = 'this';
  const otherLabel = inBatch ? 'other' : 'live';
  const lines: string[] = [];
  if (inBatch) {
    lines.push(`Mapping rejected — overlaps another row in this same batch for ${userLabel}.`);
  } else {
    lines.push(`Mapping rejected — ${userLabel} already has a live config that overlaps this period.`);
  }
  if (upload.role !== e.role) {
    lines.push(`role: ${thisLabel} ${roleLabel(upload.role)}, ${otherLabel} ${roleLabel(e.role)}`);
  }
  if ((upload.period_start || '') !== dateLabel(e.period_start)) {
    lines.push(`period_start: ${thisLabel} ${upload.period_start || '?'}, ${otherLabel} ${dateLabel(e.period_start)}`);
  }
  if ((upload.period_end || '') !== dateLabel(e.period_end)) {
    lines.push(`period_end: ${thisLabel} ${upload.period_end || '?'}, ${otherLabel} ${dateLabel(e.period_end)}`);
  }
  if ((upload.repeat || '') !== (e.repeat || '')) {
    lines.push(`repeat: ${thisLabel} ${upload.repeat || '(one-time)'}, ${otherLabel} ${e.repeat || '(one-time)'}`);
  }
  if ((upload.quota_amount || '') !== (e.quota_amount || '')) {
    lines.push(`quota: ${thisLabel} ${formatCurrency(upload.quota_amount)}, ${otherLabel} ${formatCurrency(e.quota_amount)}`);
  }
  if ((upload.base_rate || '') !== (e.base_rate || '')) {
    lines.push(`base_rate: ${thisLabel} ${formatPercent(upload.base_rate)}, ${otherLabel} ${formatPercent(e.base_rate)}`);
  }
  if ((upload.over_quota_rate || '') !== (e.over_quota_rate || '')) {
    lines.push(`over_quota_rate: ${thisLabel} ${formatPercent(upload.over_quota_rate)}, ${otherLabel} ${formatPercent(e.over_quota_rate)}`);
  }
  if (lines.length === 1) {
    // Periods overlap but every other field is identical — note that.
    const sideName = inBatch ? 'other batch row' : 'live row';
    lines.push(`(all fields identical besides id; ${sideName} ${roleLabel(e.role)} ${dateLabel(e.period_start)}..${dateLabel(e.period_end)})`);
  }
  return lines.join('\n');
}

// Quota config uploads view — reads quota_config_uploads (one row per
// imported quota), groups by (person_name, role), lets the operator
// pick a dashboard user per person and Save to map. Each upload row's
// Errors cell shows either:
//   - drift messages if the upload is mapped but its attributes have
//     diverged from the live SalesQuota row (e.g. a re-import changed
//     the period after mapping);
//   - 409 conflict detail if a Save attempt would overlap a live row.
// Mapped uploads with no drift render no Errors content.
const UploadsView: React.FC<{
  uploads: QuotaConfigUploadView[];
  users: User[];
  onChanged: () => void;
}> = ({ uploads, users, onChanged }) => {
  // Per-person staged user_id. Save iterates that person's unmapped
  // upload rows and maps each via /commissions/uploads/{id}/map.
  const [pendingByPerson, setPendingByPerson] = useState<Record<string, string>>({});
  // Per-upload-id structured 409 conflicts captured during the most
  // recent save attempt for that row. Renders inline on the row.
  const [conflictsByUploadId, setConflictsByUploadId] = useState<Record<string, SalesQuotaLinkConflict[]>>({});
  // Card-level fallback for non-conflict failures (network etc.).
  const [errorByPerson, setErrorByPerson] = useState<Record<string, string[]>>({});
  const [linking, setLinking] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const personKey = (u: QuotaConfigUploadView): string => {
    const upload = u.upload;
    return `${upload?.person_name || 'Unknown'} · ${upload?.role || ''}`;
  };

  // Drop the stale conflict / per-person error snapshot on every
  // uploads-list change. Conflicts are the verdict from one specific
  // Save attempt against a specific snapshot of upload content; once
  // any upload's content changes (re-import upserts in place by
  // operator-supplied id, so the upload row's id stays stable but
  // period/quota/rate fields can flip) the cached conflict no longer
  // describes reality. Wiping is the only way to avoid pinning a
  // pre-fix verdict to a post-fix row. The operator can re-click
  // Save to recompute. pendingByPerson (the in-progress dropdown
  // intent) is preserved for personKeys that still exist.
  React.useEffect(() => {
    const livePersonKeys = new Set<string>();
    for (const v of uploads) {
      livePersonKeys.add(personKey(v));
    }
    setConflictsByUploadId(prev => (Object.keys(prev).length === 0 ? prev : {}));
    setErrorByPerson(prev => (Object.keys(prev).length === 0 ? prev : {}));
    setPendingByPerson(prev => {
      let changed = false;
      const next: Record<string, string> = {};
      for (const k of Object.keys(prev)) {
        if (livePersonKeys.has(k)) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  const grouped: Record<string, QuotaConfigUploadView[]> = {};
  for (const v of uploads) {
    const k = personKey(v);
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(v);
  }
  const personKeys = Object.keys(grouped).sort();

  const handleSelectMapping = (key: string, userId: string) => {
    setPendingByPerson(prev => {
      const next = { ...prev };
      if (userId) next[key] = userId; else delete next[key];
      return next;
    });
    // Stale errors no longer apply once the operator changes course.
    // Clear card-level error and any per-row conflicts captured for
    // uploads under this person key.
    setErrorByPerson(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setConflictsByUploadId(prev => {
      const next = { ...prev };
      let changed = false;
      for (const v of (grouped[key] || [])) {
        const id = v.upload?.id || '';
        if (id && id in next) { delete next[id]; changed = true; }
      }
      return changed ? next : prev;
    });
  };

  const handleDelete = async (uploadId: string, label: string) => {
    if (!uploadId) return;
    if (!window.confirm(`Delete this upload (${label})?`)) return;
    try {
      await deleteCommissionsUploadsId(uploadId);
      onChanged();
    } catch (err) {
      console.error('Failed to delete upload:', err);
    }
  };

  const handleDeleteAllUnmapped = async () => {
    const unmappedCount = uploads.filter(v => !v.mapped).length;
    if (unmappedCount === 0) return;
    if (!window.confirm(`Delete ${unmappedCount} unmapped uploads? Mapped uploads (and their live config rows) are not affected.`)) return;
    setBulkDeleting(true);
    try {
      await deleteCommissionsUploads();
      onChanged();
    } catch (err) {
      console.error('Failed to delete unmapped uploads:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Save: one atomic batch call. Backend validates every candidate
  // against existing live + against every other candidate; if
  // anything would conflict, NOTHING is written and the 409 body
  // carries per-upload-id conflicts. All-or-none — no partial-
  // success outcome where some rows map and others don't.
  const handleSaveLink = async (key: string) => {
    const userId = pendingByPerson[key];
    if (!userId) return;
    const targets = (grouped[key] || []).filter(v => !v.mapped);
    if (targets.length === 0) return;
    const uploadIds = targets.map(v => v.upload?.id || '').filter(Boolean);
    if (uploadIds.length === 0) return;

    setLinking(key);
    setErrorByPerson(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setConflictsByUploadId(prev => {
      const next = { ...prev };
      let changed = false;
      for (const id of uploadIds) {
        if (id in next) { delete next[id]; changed = true; }
      }
      return changed ? next : prev;
    });

    const captureFailure = (data: any, fallback: string) => {
      const conflictMap = (data?.conflicts && typeof data.conflicts === 'object') ? data.conflicts as Record<string, SalesQuotaLinkConflict[]> : null;
      if (conflictMap && Object.keys(conflictMap).length > 0) {
        setConflictsByUploadId(prev => ({ ...prev, ...conflictMap }));
      } else {
        setErrorByPerson(prev => ({ ...prev, [key]: [data?.message || fallback] }));
      }
    };

    try {
      const resp: any = await postCommissionsUploadsMapBatch({ user_id: userId, upload_ids: uploadIds });
      if (resp?.conflicts && typeof resp.conflicts === 'object' && Object.keys(resp.conflicts).length > 0) {
        // 409 returned in-band.
        captureFailure(resp, 'Mapping rejected');
      } else {
        // Whole batch landed — clear pending and refresh.
        setPendingByPerson(prev => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        onChanged();
      }
    } catch (err: any) {
      // 409 surfaced as throw — same handling.
      captureFailure(err?.response?.data, String(err?.message || err));
    } finally {
      setLinking(null);
    }
  };

  const unmappedCount = uploads.filter(v => !v.mapped).length;

  return (
    <div className="flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
      <div className="mb-3 d-flex justify-content-end">
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleDeleteAllUnmapped}
          disabled={bulkDeleting || unmappedCount === 0}
          title="Delete every unmapped upload — mapped uploads (and their live config rows) are not affected"
        >
          <i className={`bi ${bulkDeleting ? 'bi-arrow-repeat' : 'bi-trash'} me-1`} />
          {bulkDeleting ? 'Deleting…' : `Delete all unmapped (${unmappedCount})`}
        </Button>
      </div>

      {personKeys.map(key => {
        const items = grouped[key];
        const pendingUserId = pendingByPerson[key] || '';
        const cardError = errorByPerson[key];
        const isLinking = linking === key;
        // user_id → "First Last" lookup so conflict messages can name
        // the affected dashboard user instead of dumping a UUID.
        const userNameByID = new Map<string, string>();
        for (const u of users) {
          if (u.id) userNameByID.set(u.id, [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || u.id);
        }
        // Inject per-row errors and mapping state. renderCell can't
        // close over state — read everything off the row arg. Both
        // drift messages and structured conflicts go into _errors as
        // pre-formatted multi-line strings; the renderCell just
        // renders them with the warning icon, no additional logic.
        const rows = items.map(v => {
          const u = v.upload!;
          const errs: string[] = [];
          const driftMsgs = v.drift_messages || [];
          for (const m of driftMsgs) {
            errs.push(`Live config drift — ${m}`);
          }
          const conflicts = conflictsByUploadId[u.id || ''] || [];
          for (const c of conflicts) {
            errs.push(buildConflictMessage(u, c, userNameByID));
          }
          return {
            id: u.id,
            person_name: u.person_name,
            role: u.role,
            period_start: u.period_start,
            period_end: u.period_end,
            repeat: u.repeat,
            quota_amount: u.quota_amount,
            base_rate: u.base_rate,
            over_quota_rate: u.over_quota_rate,
            // mapped reflects whether the joined live row is reachable
            // via the API, NOT just the bare mapped_quota_id pointer —
            // a soft-deleted live row leaves the pointer set but the
            // backend's join returns null. Treating that as unmapped
            // is the right UX (operator can re-map cleanly).
            mapped: !!v.mapped,
            _errors: errs,
          };
        });
        const personUploadsWithMapping = items.filter(v => !!v.mapped).length;
        const totalCount = items.length;
        return (
          <Card key={key} className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{key}</span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">
                  {personUploadsWithMapping}/{totalCount} mapped
                </span>
                <Form.Select
                  size="sm"
                  style={{ width: '200px' }}
                  value={pendingUserId}
                  disabled={isLinking}
                  onChange={(e) => handleSelectMapping(key, e.target.value)}
                >
                  <option value="">Map to user...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                  ))}
                </Form.Select>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={isLinking || !pendingUserId || personUploadsWithMapping === totalCount}
                  onClick={() => handleSaveLink(key)}
                  title={personUploadsWithMapping === totalCount ? 'All uploads here are already mapped' : 'Map every unmapped upload here to the selected user'}
                >
                  {isLinking ? (<><Spinner size="sm" animation="border" className="me-1" />Saving…</>) : 'Save'}
                </Button>
              </div>
            </Card.Header>
            {cardError && cardError.length > 0 && (
              <Alert variant="danger" className="m-2 mb-0 small" dismissible onClose={() => setErrorByPerson(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
              })}>
                {cardError.map((msg, i) => (
                  <div key={i} className={i > 0 ? 'mt-1' : ''}>
                    <i className="bi bi-exclamation-triangle-fill me-1" />
                    {msg}
                  </div>
                ))}
              </Alert>
            )}
            <DataTable
              data={rows}
              columns={[
                {
                  key: 'mapped', label: 'Status', width: 110,
                  renderCell: (q: any) => q.mapped
                    ? <Badge bg="success">Mapped</Badge>
                    : <Badge bg="secondary">Unmapped</Badge>,
                },
                { key: 'period_start', label: 'Period Start', width: 120 },
                { key: 'period_end', label: 'Period End', width: 120 },
                {
                  key: 'repeat', label: 'Repeat', width: 100,
                  renderCell: (q: any) => q.repeat || '(one-time)',
                },
                {
                  key: 'quota_amount', label: 'Quota', width: 110,
                  renderCell: (q: any) => <span className="d-block text-end">{formatCurrency(q.quota_amount)}</span>,
                },
                {
                  key: 'base_rate', label: 'Base Rate', width: 100,
                  renderCell: (q: any) => <span className="d-block text-end">{formatPercent(q.base_rate)}</span>,
                },
                {
                  key: 'over_quota_rate', label: 'Over Quota Rate', width: 130,
                  renderCell: (q: any) => <span className="d-block text-end">{formatPercent(q.over_quota_rate)}</span>,
                },
                {
                  key: 'errors', label: 'Errors', width: 460, minWidth: 280,
                  renderCell: (q: any) => {
                    const errs = (q._errors || []) as string[];
                    if (errs.length === 0) return null;
                    // Each error string may carry newlines — multi-line
                    // conflict messages put the headline on the first
                    // line and field-by-field detail on subsequent
                    // lines. Render the first line with the warning
                    // icon and the rest indented underneath.
                    return (
                      <div className="d-flex flex-column gap-2">
                        {errs.map((m, i) => {
                          const lines = m.split('\n');
                          return (
                            <div key={i} className="text-danger small" style={{ whiteSpace: 'normal' }}>
                              <div>
                                <i className="bi bi-exclamation-triangle-fill me-1" />
                                {lines[0]}
                              </div>
                              {lines.slice(1).map((line, j) => (
                                <div key={j} className="ms-3 text-body" style={{ fontFamily: 'var(--bs-font-monospace)', fontSize: '0.75rem', lineHeight: '1.4' }}>
                                  {line}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  },
                },
                {
                  key: 'actions', label: 'Actions', width: 70,
                  renderCell: (q: any) => (
                    <Button variant="outline-danger" size="sm" title="Delete this upload" onClick={() => handleDelete(q.id, `${q.period_start}..${q.period_end}`)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  ),
                },
              ] as ColumnConfig<any>[]}
              getRowKey={(q: any) => q.id || ''}
              enableColumnReorder={false}
              enableColumnResize={false}
              enableColumnVisibility={false}
            />
          </Card>
        );
      })}
    </div>
  );
};

// ============================================================================
// Quota Configuration view — quota management + import
// ============================================================================

const ConfigView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: usersData } = useAllUsers();
  const allUsers: User[] = usersData || [];

  const [selectedUserId, setSelectedUserIdState] = useState<string>(searchParams.get('config_user') || '');

  const setSelectedUserId = (userId: string) => {
    setSelectedUserIdState(userId);
    const params = new URLSearchParams(searchParams);
    if (userId) {
      params.set('config_user', userId);
    } else {
      params.delete('config_user');
    }
    setSearchParams(params, { replace: true });
  };
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [editingQuota, setEditingQuota] = useState<SalesQuota | null>(null);

  // Auth user + their preferences power column-state persistence on
  // the DataTables in this view.
  const { user: authUser } = useSelector((state: RootState) => state.auth);
  const { data: authUserData } = useGetUsersId(authUser?.id || '', { query: { enabled: !!authUser?.id } });

  const { data: quotasData, isLoading: quotasLoading } = useGetUsersUserIdSalesQuota(
    selectedUserId, { query: { enabled: !!selectedUserId && selectedUserId !== '_uploads' } }
  );
  const quotas: SalesQuota[] = (quotasData as any)?.data || [];

  // Uploads view feeds off the dedicated /commissions/uploads endpoint —
  // never sales_quotas, since uploads live in their own table now and
  // are not driven by user_id at all.
  const { data: uploadsData, isLoading: uploadsLoading } = useGetCommissionsUploads({
    query: { enabled: selectedUserId === '_uploads' },
  });
  const uploads: QuotaConfigUploadView[] = (uploadsData as any)?.data || [];

  const putQuotaMutation = usePutUsersUserIdSalesQuota();

  const handleSaveQuota = async (quota: SalesQuota) => {
    if (!selectedUserId) return;
    // Let errors propagate so the modal can render them next to the
    // Save button. Swallowing here just makes Save look broken — the
    // user clicks, the request fails, and nothing changes on screen.
    await putQuotaMutation.mutateAsync({ userId: selectedUserId, data: quota });
    queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(selectedUserId) });
    setShowQuotaModal(false);
    setEditingQuota(null);
  };

  const handleDeleteQuota = async (quota: SalesQuota) => {
    if (!quota.id) return;
    if (!window.confirm(`Delete this ${quota.role || 'quota'} (${quota.period_start}..${quota.period_end})?`)) return;
    try {
      await deleteCommissionsQuotasId(quota.id);
      // Invalidate both the per-user view (when one is selected) and the
      // unlinked-quota bucket so either landing place reflects the
      // deletion immediately.
      if (selectedUserId) {
        queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(selectedUserId) });
      }
      queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey('_unlinked') });
    } catch (err) {
      console.error('Failed to delete quota:', err);
    }
  };

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPeople, setImportPeople] = useState<ImportPerson[] | null>(null);
  // Success result is the simple imported count. Failure populates
  // importErrors instead — those stay alongside importPeople so the
  // mapping table can render per-row error pills until the operator
  // fixes the source file or DB and re-clicks Import.
  const [importResult, setImportResult] = useState<{ imported?: number } | null>(null);
  const [importErrors, setImportErrors] = useState<Array<{
    mapping_index: number;
    quota_index: number;
    person_name?: string;
    role?: string;
    quota_id?: string;
    period_start?: string;
    period_end?: string;
    message: string;
  }> | null>(null);
  const [importing, setImporting] = useState(false);

  const handleFileSelected = async (file: File) => {
    try {
      const text = await file.text();
      const config = JSON.parse(text);
      const people: ImportPerson[] = [];

      const processSection = (section: Record<string, any> | undefined, role: 'salesperson' | 'sales_lead') => {
        if (!section) return;
        for (const cfg of Object.values(section)) {
          people.push({
            configName: cfg.Person?.Name || 'Unknown',
            role,
            quotas: (cfg.Quotas || []).map((q: any) => ({
              id: q.ID || q.Id || q.id || '',
              start: q.Start, end: q.End, repeat: q.Repeat || '',
              quota: q.Quota, baseRate: q.BaseRate, overQuotaRate: q.OverQuotaRate,
            })),
          });
        }
      };

      processSection(config.SalesPeople, 'salesperson');
      processSection(config.SalesLeads, 'sales_lead');
      setImportPeople(people);
      setImportResult(null);
    } catch (err) {
      setImportErrors([{
        mapping_index: -1,
        quota_index: -1,
        message: `Failed to parse file: ${err}`,
      }]);
    }
  };

  const handleImportSubmit = async () => {
    if (!importPeople) return;

    setImporting(true);
    setImportResult(null);
    setImportErrors(null);
    // The orval/axios client may surface a 400 either as a throw OR as a
    // normal return with the parsed body, depending on validateStatus.
    // Treat both paths uniformly: errors populated → render them, no
    // errors → success.
    const captureErrors = (data: any, fallbackMsg: string) => {
      if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        setImportErrors(data.errors);
      } else {
        setImportErrors([{
          mapping_index: -1,
          quota_index: -1,
          message: fallbackMsg,
        }]);
      }
    };

    try {
      // Import is name-only; linking to dashboard users happens as a
      // separate step from the Unlinked Quotas view. Mixing the two
      // here turned out to be a footgun — operators picked a user in
      // the import row, hit Save, and overlaps slipped through
      // because the import path bypassed the link-time conflict UI.
      const result: any = await postCommissionsImportConfig({
        mappings: importPeople.map(p => ({
          person_name: p.configName,
          role: p.role,
          quotas: p.quotas.map(q => ({
            id: q.id || '',
            start: q.start, end: q.end, repeat: q.repeat || undefined,
            quota: q.quota, base_rate: q.baseRate, over_quota_rate: q.overQuotaRate,
          })),
        })),
      });
      if (result?.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        // 400 returned in-band — still keep the mapping table open
        // and pin the errors to the affected rows.
        captureErrors(result, 'import rejected');
      } else {
        // True 200: every row landed. Invalidate the uploads query
        // so the Quota Config Uploads view picks up the new rows
        // immediately. Also invalidate the per-user quotas query
        // (in case a user view was open) — harmless when it isn't.
        setImportResult({ imported: result?.imported });
        setImportPeople(null);
        setImportErrors(null);
        setShowImportModal(false);
        queryClient.invalidateQueries({ queryKey: getGetCommissionsUploadsQueryKey() });
        if (selectedUserId && selectedUserId !== '_uploads') {
          queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(selectedUserId) });
        }
      }
    } catch (err: any) {
      // 400 surfaced as a throw — same handling.
      captureErrors(err?.response?.data, String(err?.message || err));
    } finally {
      setImporting(false);
    }
  };

  // Group per-row errors by mapping_index so each row in the table
  // can render its own slice. quota_index is preserved on each error
  // so the Errors column can name the offending period.
  const errorsByRow = useMemo(() => {
    const m = new Map<number, typeof importErrors>();
    if (!importErrors) return m;
    for (const e of importErrors) {
      const list = m.get(e.mapping_index) || [];
      list.push(e);
      m.set(e.mapping_index, list);
    }
    return m;
  }, [importErrors]);

  const activeUsers = allUsers.filter(u => u.active !== false);

  // Closing the modal resets all import-related transient state and
  // refreshes the currently-selected view (uploads or per-user
  // quotas) so any rows that landed during this session show up
  // immediately. Invalidating on every close — including cancel —
  // is harmless and matches the expectation that opening/closing
  // the import dialog leaves the page in a known-fresh state.
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportPeople(null);
    setImportErrors(null);
    queryClient.invalidateQueries({ queryKey: getGetCommissionsUploadsQueryKey() });
    if (selectedUserId && selectedUserId !== '_uploads') {
      queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(selectedUserId) });
    }
  };

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
      <Modal
        show={showImportModal}
        onHide={closeImportModal}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {importPeople
              ? `Import Quota Config — Review (${importPeople.length})`
              : 'Import Quota Config'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', maxHeight: '70vh' }}>
          {!importPeople ? (
            <div className="d-flex flex-column gap-3">
              <Form.Group>
                <Form.Label>Select a quota config JSON file to import.</Form.Label>
                <Form.Control
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const file = input.files?.[0];
                    if (file) handleFileSelected(file);
                    input.value = '';
                  }}
                />
              </Form.Group>
              {importErrors && importErrors.length > 0 && (
                <Alert variant="danger" className="mb-0">
                  <div className="fw-semibold mb-1">Import rejected — no rows were written.</div>
                  {importErrors.map((e, i) => (
                    <div key={i} className="small">{e.message}</div>
                  ))}
                </Alert>
              )}
            </div>
          ) : (
            <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
              <DataTable
                data={importPeople.map((p, i) => ({ ...p, _idx: i, _errors: errorsByRow.get(i) || [] }))}
                columns={[
                  {
                    key: 'configName', label: 'Config Name', width: 220, minWidth: 160, sortable: true,
                    renderCell: (p: any) => p.configName,
                  },
                  {
                    key: 'role', label: 'Role', width: 130, minWidth: 110, sortable: true,
                    renderCell: (p: any) => (
                      <Badge bg={p.role === 'sales_lead' ? 'info' : 'primary'}>
                        {p.role === 'sales_lead' ? 'Sales Lead' : 'Salesperson'}
                      </Badge>
                    ),
                  },
                  {
                    key: 'quotas_count', label: 'Quotas', width: 90, minWidth: 70, sortable: true,
                    renderCell: (p: any) => <span className="d-block text-end">{p.quotas.length}</span>,
                    sortValue: (p: any) => p.quotas.length,
                  },
                  {
                    // renderCell reads errors off the row itself (p._errors)
                    // rather than a closure over external state — DataTable
                    // captures column.renderCell once via useState init, so a
                    // closure over a state map would stay stale across error
                    // updates. The error data is injected into the row by the
                    // data prop below.
                    key: 'errors', label: 'Errors', width: 360, minWidth: 220, sortable: false,
                    renderCell: (p: any) => {
                      const rowErrors = (p._errors || []) as Array<any>;
                      if (rowErrors.length === 0) return null;
                      return (
                        <div className="d-flex flex-column gap-1">
                          {rowErrors.map((e: any, i: number) => (
                            <div key={i} className="text-danger small" style={{ whiteSpace: 'normal' }}>
                              <i className="bi bi-exclamation-triangle-fill me-1" />
                              {e.quota_index >= 0 && e.period_start && e.period_end
                                ? <><strong>{e.period_start}..{e.period_end}</strong>: </>
                                : null}
                              {e.message}
                            </div>
                          ))}
                        </div>
                      );
                    },
                    sortValue: (p: any) => (p._errors?.length || 0),
                  },
                ] as ColumnConfig<any>[]}
                getRowKey={(p: any) => String(p._idx)}
                maxHeight="100%"
                enableStickyHeader={true}
                enableColumnReorder={true}
                enableColumnResize={true}
                enableColumnVisibility={true}
                preferencesKey="commission_import_mapping"
                userId={authUser?.id}
                userPreferences={authUserData?.preferences}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeImportModal} disabled={importing}>
            {importPeople ? 'Cancel' : 'Close'}
          </Button>
          {importPeople && (
            <Button variant="primary" onClick={handleImportSubmit} disabled={importing}>
              {importing ? 'Importing...' : `Import ${importPeople.length} People`}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {importResult && (
        <Alert variant="success" dismissible onClose={() => setImportResult(null)}>
          Imported {importResult.imported || 0} quotas.
        </Alert>
      )}

      {/* Toolbar */}
      <div className="mb-3 d-flex gap-3 align-items-center flex-wrap">
        <Form.Select size="sm" style={{ width: '250px' }} value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
          <option value="">Select a user...</option>
          <option value="_uploads">-- Quota Config Uploads --</option>
          {activeUsers.map(u => (
            <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
          ))}
        </Form.Select>

        <Button variant="outline-secondary" size="sm" onClick={() => {
          setImportErrors(null);
          setImportPeople(null);
          setShowImportModal(true);
        }}>
          <i className="bi bi-upload me-1"></i>Import Quota Config
        </Button>

        {selectedUserId && selectedUserId !== '_uploads' && (
          <Button variant="primary" size="sm" onClick={() => {
            setEditingQuota({
              user_id: selectedUserId,
              role: 'salesperson' as any,
              period_start: new Date().toLocaleDateString('en-CA'),
              period_end: '',
              repeat: 'monthly',
              quota_amount: '',
              base_rate: '0.1',
              over_quota_rate: '0.12',
            });
            setShowQuotaModal(true);
          }}>
            <i className="bi bi-plus me-1"></i>Add Quota
          </Button>
        )}
      </div>

      {!selectedUserId ? (
        <Alert variant="info">Select a user to view their sales quotas, or open the Quota Config Uploads view to map imported entries.</Alert>
      ) : selectedUserId === '_uploads' ? (
        uploadsLoading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : uploads.length === 0 ? (
          <Alert variant="info">No quota config uploads. Use Import Quota Config to add some.</Alert>
        ) : (
          <UploadsView
            uploads={uploads}
            users={activeUsers}
            onChanged={() => queryClient.invalidateQueries({ queryKey: getGetCommissionsUploadsQueryKey() })}
          />
        )
      ) : quotasLoading ? (
        <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
      ) : quotas.length === 0 ? (
        <Alert variant="info">No sales quotas configured for this user.</Alert>
      ) : (
        <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: '240px' }}>
        <DataTable
          data={quotas}
          columns={[
            {
              key: 'role', label: 'Role', width: 110,
              renderCell: (q) => <Badge bg={q.role === 'sales_lead' ? 'info' : 'primary'}>{q.role === 'sales_lead' ? 'Sales Lead' : 'Salesperson'}</Badge>,
            },
            { key: 'period_start', label: 'Period Start', width: 120 },
            { key: 'period_end', label: 'Period End', width: 120 },
            {
              key: 'repeat', label: 'Repeat', width: 100,
              renderCell: (q) => q.repeat || '(one-time)',
            },
            {
              key: 'quota_amount', label: 'Quota', width: 110,
              renderCell: (q) => <span className="d-block text-end">{formatCurrency(q.quota_amount)}</span>,
            },
            {
              key: 'base_rate', label: 'Base Rate', width: 100,
              renderCell: (q) => <span className="d-block text-end">{formatPercent(q.base_rate)}</span>,
            },
            {
              key: 'over_quota_rate', label: 'Over Quota Rate', width: 130,
              renderCell: (q) => <span className="d-block text-end">{formatPercent(q.over_quota_rate)}</span>,
            },
            {
              key: 'actions', label: 'Actions', width: 130,
              renderCell: (q) => (
                <div className="d-flex gap-1">
                  <Button variant="outline-primary" size="sm" title="Edit quota" onClick={() => { setEditingQuota(q); setShowQuotaModal(true); }}>
                    <i className="bi bi-pencil"></i>
                  </Button>
                  <Button variant="outline-danger" size="sm" title="Delete quota" onClick={() => handleDeleteQuota(q)}>
                    <i className="bi bi-trash"></i>
                  </Button>
                </div>
              ),
            },
          ] as ColumnConfig<any>[]}
          getRowKey={(q) => q.id || ''}
          enableColumnReorder={false}
          enableColumnResize={true}
          enableColumnVisibility={false}
        />
        </div>
      )}

      <QuotaEditModal show={showQuotaModal} quota={editingQuota} onHide={() => { setShowQuotaModal(false); setEditingQuota(null); }} onSave={handleSaveQuota} />
    </div>
  );
};

// ============================================================================
// Quota Edit Modal
// ============================================================================

const QuotaEditModal: React.FC<{
  show: boolean;
  quota: SalesQuota | null;
  onHide: () => void;
  onSave: (quota: SalesQuota) => Promise<void> | void;
}> = ({ show, quota, onHide, onSave }) => {
  const [formData, setFormData] = useState<SalesQuota>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (quota) setFormData({ ...quota });
    // Re-opening the modal (or switching from edit to add) clears
    // any error from a previous attempt.
    setSaveError(null);
    setSaving(false);
  }, [quota, show]);

  if (!show) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(formData);
    } catch (err: any) {
      // Backend Validate() errors come back as 400 with a "message"
      // field. Fall back to the JS Error message or a generic string
      // so the operator always gets something readable.
      setSaveError(
        err?.response?.data?.message
          || err?.message
          || 'Failed to save quota'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{formData.id ? 'Edit Quota' : 'New Quota'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select value={(formData.role as string) || 'salesperson'} onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}>
              <option value="salesperson">Salesperson</option>
              <option value="sales_lead">Sales Lead</option>
            </Form.Select>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Period Start (inclusive)</Form.Label>
                <Form.Control type="date" value={formData.period_start || ''} onChange={(e) => setFormData({ ...formData, period_start: e.target.value })} />
                <Form.Text className="text-muted">
                  Quota begins at 00:00 (business time) on this date.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Period End (exclusive)</Form.Label>
                <Form.Control type="date" value={formData.period_end || ''} onChange={(e) => setFormData({ ...formData, period_end: e.target.value })} />
                <Form.Text className="text-muted">
                  Quota ends at 00:00 (business time) on this date — i.e. the
                  last day covered is the one before. Must be after Period Start.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Repeat</Form.Label>
            <Form.Select value={formData.repeat || ''} onChange={(e) => setFormData({ ...formData, repeat: e.target.value })}>
              <option value="">One-time (single period)</option>
              <option value="monthly">Monthly (repeats each month in range)</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Quota Amount ($)</Form.Label>
            <Form.Control type="number" step="0.01" value={formData.quota_amount || ''} onChange={(e) => setFormData({ ...formData, quota_amount: e.target.value })} />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Base Commission Rate</Form.Label>
                <Form.Control type="number" step="0.01" value={formData.base_rate ? (parseFloat(formData.base_rate) * 100).toString() : ''} onChange={(e) => setFormData({ ...formData, base_rate: (parseFloat(e.target.value) / 100).toString() })} placeholder="e.g. 10 for 10%" />
                <Form.Text className="text-muted">Enter as percentage</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Over Quota Rate</Form.Label>
                <Form.Control type="number" step="0.01" value={formData.over_quota_rate ? (parseFloat(formData.over_quota_rate) * 100).toString() : ''} onChange={(e) => setFormData({ ...formData, over_quota_rate: (parseFloat(e.target.value) / 100).toString() })} placeholder="e.g. 12 for 12%" />
                <Form.Text className="text-muted">Enter as percentage</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      {saveError && (
        <Alert variant="danger" className="mx-3 mb-0" dismissible onClose={() => setSaveError(null)}>
          <i className="bi bi-exclamation-triangle-fill me-1" />
          {saveError}
        </Alert>
      )}
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? (<><Spinner size="sm" animation="border" className="me-1" />Saving…</>) : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommissionsPage;
